/**
 * A small, total Markdown reader for the vendored operator guides.
 *
 * The console does not take a Markdown dependency for this. The guides in
 * `content/` are written by us, in a narrow subset — headings, paragraphs,
 * lists, fenced code, pipe tables, block quotes, rules — and a parser for that
 * subset is smaller than the smallest library that would render it, ships no
 * HTML pass-through, and cannot be surprised by a construct we never write. A
 * test asserts the subset holds across all three files, so a guide that grows a
 * construct this cannot read fails the suite rather than rendering as noise.
 *
 * Nothing here interprets raw HTML: `<` is text. That is the property that lets
 * a guide be rendered without sanitising it.
 */

export interface InlineText { kind: 'text'; value: string }
export interface InlineCode { kind: 'code'; value: string }
export interface InlineStrong { kind: 'strong'; children: Inline[] }
export interface InlineEmphasis { kind: 'em'; children: Inline[] }
export interface InlineLink { kind: 'link'; href: string; children: Inline[] }
export type Inline = InlineText | InlineCode | InlineStrong | InlineEmphasis | InlineLink

export interface HeadingBlock { kind: 'heading'; level: 1 | 2 | 3 | 4 | 5 | 6; id: string; text: string; children: Inline[] }
export interface ParagraphBlock { kind: 'paragraph'; children: Inline[] }
export interface ListItem { children: Inline[]; sub: ListItem[] }
export interface ListBlock { kind: 'list'; ordered: boolean; items: ListItem[] }
export interface CodeBlock { kind: 'code'; language: string; code: string }
export interface TableBlock { kind: 'table'; head: Inline[][]; rows: Inline[][][] }
export interface QuoteBlock { kind: 'quote'; blocks: Block[] }
export interface RuleBlock { kind: 'rule' }
export type Block = HeadingBlock | ParagraphBlock | ListBlock | CodeBlock | TableBlock | QuoteBlock | RuleBlock

/**
 * GitHub's heading anchor rule, which is the one the guides were written
 * against: lower-case, drop anything that is not a letter, digit, space or
 * hyphen, then turn spaces into hyphens. An em dash therefore collapses to
 * nothing and leaves the doubled hyphen the source files already link to
 * (`#3-path-a--the-console`).
 */
export function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/`|\*|_/g, '')
    .replace(/[^\p{Letter}\p{Number} -]/gu, '')
    .trim()
    .replace(/ /g, '-')
}

function inlineText(nodes: Inline[]): string {
  return nodes.map((node) => (node.kind === 'text' || node.kind === 'code' ? node.value : inlineText(node.children))).join('')
}

/** Flatten inline nodes back to their plain text — headings, ids, search. */
export function plainText(nodes: Inline[]): string {
  return inlineText(nodes)
}

const INLINE = /(`[^`]+`)|(\[[^\]]*\]\([^)\s]*\))|(\*\*[^*]+\*\*)|(\*[^*\n]+\*)/

/** Inline parsing, innermost-first, with everything unmatched staying text. */
export function parseInline(source: string): Inline[] {
  const nodes: Inline[] = []
  let rest = source
  for (;;) {
    const match = INLINE.exec(rest)
    if (!match || match.index === undefined) break
    if (match.index > 0) nodes.push({ kind: 'text', value: rest.slice(0, match.index) })
    const token = match[0]
    if (token.startsWith('`')) {
      nodes.push({ kind: 'code', value: token.slice(1, -1) })
    } else if (token.startsWith('[')) {
      const split = token.indexOf('](')
      nodes.push({ kind: 'link', href: token.slice(split + 2, -1), children: parseInline(token.slice(1, split)) })
    } else if (token.startsWith('**')) {
      nodes.push({ kind: 'strong', children: parseInline(token.slice(2, -2)) })
    } else {
      nodes.push({ kind: 'em', children: parseInline(token.slice(1, -1)) })
    }
    rest = rest.slice(match.index + token.length)
  }
  if (rest) nodes.push({ kind: 'text', value: rest })
  return nodes
}

const HEADING = /^(#{1,6}) +(.*)$/
const FENCE = /^ *```(.*)$/
const RULE = /^(?:-{3,}|\*{3,}|_{3,})$/
const BULLET = /^( *)[-*] +(.*)$/
const NUMBER = /^( *)(\d+)\. +(.*)$/
const TABLE_ROW = /^ *\|(.*)\| *$/
const TABLE_DIVIDER = /^ *\|[ :|-]+\| *$/
const QUOTE = /^ *> ?(.*)$/

function cells(line: string): string[] {
  return (TABLE_ROW.exec(line)?.[1] ?? '').split('|').map((cell) => cell.trim())
}

/**
 * Parse a guide into blocks.
 *
 * `usedIds` keeps heading anchors unique within one document the way GitHub
 * does — a repeated heading gets `-1`, `-2` — so a duplicate cannot silently
 * steal another section's in-page link.
 */
export function parseMarkdown(source: string): Block[] {
  const lines = source.replace(/\r\n/g, '\n').split('\n')
  const blocks: Block[] = []
  const usedIds = new Map<string, number>()
  let index = 0

  const uniqueId = (text: string) => {
    const base = slugify(text)
    const seen = usedIds.get(base) ?? 0
    usedIds.set(base, seen + 1)
    return seen === 0 ? base : `${base}-${seen}`
  }

  while (index < lines.length) {
    const line = lines[index] ?? ''

    if (!line.trim()) { index += 1; continue }

    const fence = FENCE.exec(line)
    if (fence) {
      const code: string[] = []
      index += 1
      while (index < lines.length && !FENCE.test(lines[index] ?? '')) { code.push(lines[index] ?? ''); index += 1 }
      index += 1
      blocks.push({ kind: 'code', language: (fence[1] ?? '').trim(), code: code.join('\n') })
      continue
    }

    const heading = HEADING.exec(line)
    if (heading) {
      const children = parseInline(heading[2] ?? '')
      const text = plainText(children)
      blocks.push({ kind: 'heading', level: (heading[1] ?? '#').length as HeadingBlock['level'], id: uniqueId(text), text, children })
      index += 1
      continue
    }

    if (RULE.test(line.trim())) { blocks.push({ kind: 'rule' }); index += 1; continue }

    if (TABLE_ROW.test(line) && TABLE_DIVIDER.test(lines[index + 1] ?? '')) {
      const head = cells(line).map(parseInline)
      index += 2
      const rows: Inline[][][] = []
      while (index < lines.length && TABLE_ROW.test(lines[index] ?? '')) {
        rows.push(cells(lines[index] ?? '').map(parseInline))
        index += 1
      }
      blocks.push({ kind: 'table', head, rows })
      continue
    }

    if (QUOTE.test(line)) {
      // Only genuinely marked lines are quoted. Lazy continuation — an
      // unmarked line under a quote — is not a construct the guides use, and
      // supporting it would let a quote swallow the paragraph after it.
      const quoted: string[] = []
      while (index < lines.length && QUOTE.test(lines[index] ?? '')) {
        quoted.push(QUOTE.exec(lines[index] ?? '')?.[1] ?? '')
        index += 1
      }
      blocks.push({ kind: 'quote', blocks: parseMarkdown(quoted.join('\n')) })
      continue
    }

    if (BULLET.test(line) || NUMBER.test(line)) {
      const ordered = NUMBER.test(line)
      const items: ListItem[] = []
      while (index < lines.length) {
        const current = lines[index] ?? ''
        const bullet = BULLET.exec(current)
        const numbered = NUMBER.exec(current)
        // A bulleted list under a numbered one (or the reverse) is a second
        // list, not a continuation of the first.
        if ((bullet ?? numbered) && (bullet?.[1] ?? numbered?.[1] ?? '').length === 0 && Boolean(numbered) !== ordered) break
        if (!bullet && !numbered) {
          // A blank line inside a list is a separator, not a terminator, but a
          // blank line followed by anything unindented ends the list.
          if (!current.trim() && (lines[index + 1] ?? '').match(/^(?: *[-*] | *\d+\. )/)) { index += 1; continue }
          if (!current.trim() || !/^ {2,}\S/.test(current)) break
          // A continuation line belongs to the item above it.
          const last = items.at(-1)
          const target = last?.sub.at(-1) ?? last
          if (target) target.children = parseInline(`${plainText(target.children)} ${current.trim()}`)
          index += 1
          continue
        }
        const indent = (bullet?.[1] ?? numbered?.[1] ?? '').length
        const text = bullet?.[2] ?? numbered?.[3] ?? ''
        const item: ListItem = { children: parseInline(text), sub: [] }
        if (indent >= 2 && items.length > 0) items.at(-1)!.sub.push(item)
        else items.push(item)
        index += 1
      }
      blocks.push({ kind: 'list', ordered, items })
      continue
    }

    const paragraph: string[] = []
    while (index < lines.length) {
      const current = lines[index] ?? ''
      if (!current.trim() || HEADING.test(current) || FENCE.test(current) || QUOTE.test(current) || BULLET.test(current) || NUMBER.test(current) || TABLE_ROW.test(current) || RULE.test(current.trim())) break
      paragraph.push(current.trim())
      index += 1
    }
    blocks.push({ kind: 'paragraph', children: parseInline(paragraph.join(' ')) })
  }

  return blocks
}

export interface TocEntry { id: string; text: string; level: number }

/** The on-page contents: section headings only, never the document title. */
export function tableOfContents(blocks: Block[]): TocEntry[] {
  return blocks.flatMap((block) => (block.kind === 'heading' && block.level >= 2 && block.level <= 3 ? [{ id: block.id, text: block.text, level: block.level }] : []))
}

/** The document title — the single level-one heading each guide opens with. */
export function documentTitle(blocks: Block[]): string | null {
  const heading = blocks.find((block): block is HeadingBlock => block.kind === 'heading' && block.level === 1)
  return heading?.text ?? null
}
